<template>
  <div id="chat-oferta" class="chat-container">
    <div class="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
      <h4 class="mb-0">Chat Oferta : {{ codigo }}</h4>
      <b-button 
        variant="outline-success" 
        size="sm" 
        @click="goToOffer" 
        v-b-tooltip.hover 
        title="Ver ficha completa de la oferta"
        class="shadow-sm font-weight-bold"
      >
        <i class="fas fa-external-link-alt mr-1"></i> Ir a la Oferta
      </b-button>
    </div>

    <div class="chat-window">
      <div class="messages-display" ref="messagesDisplay">
        <template v-for="(msg, index) in messages">
          <!-- Separador de fecha -->
          <div v-if="shouldShowDate(index)" :key="'date-' + index" class="date-divider">
            {{ formatDate(getMsgDate(msg)) }}
          </div>

          <!-- Mensaje -->
          <div :key="'msg-' + index" :class="getMessageClass(msg)" class="message-wrapper">
            <div class="message-bubble">
              <span v-if="msg.type === 'CHAT'" class="message-sender">
                {{ getDisplayName(msg.sender) }} :
              </span>
              <span class="message-content" v-html="formatMentionedContent(msg.content)"></span>
            </div>
            <b-button 
              v-if="msg.type === 'CHAT' && msg.sender === username" 
              variant="link" 
              class="delete-msg-btn text-danger p-0 ml-1" 
              @click="confirmDelete(msg)"
              v-b-tooltip.hover
              title="Borrar mensaje"
            >
              <i class="fas fa-trash-alt"></i>
            </b-button>
          </div>
        </template>
      </div>

      <div class="message-input-section position-relative">
        <!-- Autocomplete Dropdown -->
        <b-list-group v-if="showAutocomplete && filteredEmployees.length > 0" class="autocomplete-dropdown">
          <b-list-group-item
            v-for="emp in filteredEmployees"
            :key="emp.username"
            button
            @mousedown.prevent="selectMention(emp)"
            class="d-flex align-items-center"
          >
            <b-avatar :src="emp.foto" size="sm" class="mr-2"></b-avatar>
            <span>{{ emp.nombre }} {{ emp.apellidos || '' }}</span>
          </b-list-group-item>
        </b-list-group>

        <input
          type="text"
          v-model="messageContent"
          placeholder="Escribe tu mensaje..."
          @keyup.enter="sendMessage"
          @input="onInput"
          @keydown="onKeyDown"
          ref="messageInput"
        />
        <b-button @click="sendMessage" variant="outline-primary"><i class="fas fa-play"></i></b-button>
      </div>
    </div>
  </div>
</template>

<script>
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import debounce from 'lodash/debounce';
import { api1, WEBSOCKET_PROJECT_URL } from "@/lib/http";

export default {
  name: 'ChatOferta',
  props: {
    ofertaId: { type: String, required: true },
    username: { type: String, required: true },
    codigo: { type: String, required: false, default: '' },
  },
  data() {
    return {
      connecting: false,
      subscription: null,
      stompClient: null,
      nombreEmpleado: '',
      isConnected: false,
      messageContent: '',
      messages: [],
      // Auto-complete
      showAutocomplete: false,
      filteredEmployees: [],
      mentionSearch: '',
      cursorPosition: 0,
    };
  },
  watch: {
    ofertaId: { immediate: true, handler: 'reconnectChat' },
    username: 'reconnectChat',
  },
  methods: {
    onInput(e) {
      this.checkMention(e.target);
    },
    goToOffer() {
      if (this.ofertaId) {
        // Cerramos los posibles modales
        this.$bvModal.hide("modal-chat-oferta");
        this.$bvModal.hide("modal-chat-oferta-list");
        // Navegamos al detalle de la oferta
        this.$router.push({ name: 'detalle-oferta', params: { id: this.ofertaId } }).catch(() => {});
      }
    },
    onKeyDown(e) {
      if (this.showAutocomplete && e.key === 'Escape') {
        this.showAutocomplete = false;
      }
    },
    checkMention(input) {
      const value = input.value;
      const cursorPos = input.selectionStart;
      this.cursorPosition = cursorPos;
      const lastAt = value.lastIndexOf('@', cursorPos - 1);
      
      if (lastAt !== -1) {
        const textAfterAt = value.substring(lastAt + 1, cursorPos);
        if (!/[\n]/.test(textAfterAt)) {
          this.mentionSearch = textAfterAt;
          this.debouncedSearch(textAfterAt);
        } else {
          this.showAutocomplete = false;
        }
      } else {
        this.showAutocomplete = false;
      }
    },
    debouncedSearch: debounce(function(term) {
      if (!term || term.length < 2) {
        this.showAutocomplete = false;
        return;
      }
      this.fetchEmployees(term);
    }, 300),
    
    async fetchEmployees(term) {
      try {
        const response = await api1.get(`api/auth/v204/empleado/search?term=${encodeURIComponent(term)}`);
        const uniqueEmployees = [];
        const seenUsernames = new Set();
        if (Array.isArray(response.data)) {
          for (const emp of response.data) {
            if (emp.username && !seenUsernames.has(emp.username) && emp.estado !== false) {
              seenUsernames.add(emp.username);
              uniqueEmployees.push(emp);
            }
          }
        }
        this.filteredEmployees = uniqueEmployees;
        this.showAutocomplete = this.filteredEmployees.length > 0;
      } catch (error) {
        console.error("Error searching employees", error);
        this.showAutocomplete = false;
      }
    },
    
    selectMention(emp) {
      const value = this.messageContent;
      const lastAt = value.lastIndexOf('@', this.cursorPosition - 1);
      if (lastAt !== -1) {
        const before = value.substring(0, lastAt);
        const after = value.substring(this.cursorPosition);
        const mentionName = `${emp.nombre}${emp.apellidos ? ' ' + emp.apellidos : ''}`;
        const mentionTag = `@[${emp.username}:${mentionName}]`;
        this.messageContent = `${before}${mentionTag} ${after}`;
        this.showAutocomplete = false;
        this.$nextTick(() => {
          this.$refs.messageInput.focus();
        });
      }
    },

    formatMentionedContent(content) {
      if (!content) return '';
      // Escape HTML to prevent XSS
      const escaped = content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
      
      // Replace @[username:Name] with a styled span
      return escaped.replace(/@\[\w+:([^\]]+)\]/g, '<span class="text-primary font-weight-bold">@$1</span>');
    },

    getMsgDate(msg) {
      const t = msg?.createdAt || msg?.timestamp || msg?._receivedAt || null;
      return t ? new Date(t) : null;
    },
    isSameDay(a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    },
    shouldShowDate(index) {
      const curr = this.getMsgDate(this.messages[index]);
      if (!curr) return index === 0;
      if (index === 0) return true;
      const prev = this.getMsgDate(this.messages[index - 1]);
      if (!prev) return true;
      return !this.isSameDay(prev, curr);
    },
    formatDate(d) {
      return d.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesDisplay;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    getMessageClass(msg) {
      if (msg.type === 'JOIN' || msg.type === 'LEAVE') return 'system-message';
      return msg.sender === this.username ? 'my-message' : 'other-message';
    },

    async cargarNombreEmpleado() {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.username) return;
      try {
        const res = await api1.get(`api/dekra/v204/empleado/username/${user.username}`);
        this.nombreEmpleado = res?.data?.nombre || '';
      } catch (error) {
        console.error('Error al obtener el nombre del empleado:', error);
      }
    },

    async disconnect() {
      try {
        if (this.stompClient?.connected) {
          this.stompClient.publish({
            destination: `/app/chat.addUser.oferta/${this.ofertaId}`,
            body: JSON.stringify({ sender: this.username, type: 'LEAVE' }),
          });
        }
      } catch (e) {void e;}
      this.subscription?.unsubscribe();
      this.subscription = null;
      if (this.stompClient) {
        await this.stompClient.deactivate();
      }
      this.stompClient = null;
      this.isConnected = false;
      this.connecting = false;
    },

    async connect() {
      if (!this.username || !this.ofertaId) {
        this.isConnected = false;
        return;
      }
      if (this.connecting || this.isConnected) return;
      this.connecting = true;

      // Cargar histórico inmediatamente
      try {
        const response = await api1.get(`api/chat/oferta/history/${this.ofertaId}`);
        this.messages = response.data;
        this.scrollToBottom();
      } catch (error) {
        console.error('Error al cargar mensajes históricos:', error);
      }

      const socketUrl = WEBSOCKET_PROJECT_URL || (process.env.VUE_APP_API1_URL ? `${process.env.VUE_APP_API1_URL}/ws-proyecto` : '');
      const socket = new SockJS(socketUrl, null, {
        transports: ['websocket', 'xhr-streaming', 'xhr-polling']
      });
      const client = new Client({
        webSocketFactory: () => socket,
        debug: () => {},
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
      });

      client.onConnect = async () => {
        this.isConnected = true;
        this.connecting = false;

        // Suscripción
        this.subscription = client.subscribe(
          `/topic/public.oferta.${this.ofertaId}`,
          (message) => {
            const newMessage = JSON.parse(message.body);
            if (newMessage.type === 'DELETE') {
              this.messages = this.messages.filter(m => m.id != newMessage.id);
              return;
            }
            if (!newMessage.createdAt && !newMessage.timestamp) {
              newMessage._receivedAt = new Date().toISOString();
            }
            this.messages.push(newMessage);
            this.scrollToBottom();
          }
        );

        // JOIN
        client.publish({
          destination: `/app/chat.addUser.oferta/${this.ofertaId}`,
          body: JSON.stringify({ sender: this.username, type: 'JOIN' }),
        });
      };

      client.onStompError = () => {
        this.isConnected = false;
        this.connecting = false;
      };
      
      this.stompClient = client;
      client.activate();
    },

    async reconnectChat() {
      await this.disconnect();
      await this.connect();
    },

    sendMessage() {
      const content = (this.messageContent || '').trim();
      if (!content || !this.stompClient?.connected) return;
      
      const chatMessage = {
        sender: this.username,
        content,
        type: 'CHAT',
      };
      
      try {
        this.stompClient.publish({
          destination: `/app/chat.sendMessage.oferta/${this.ofertaId}`,
          body: JSON.stringify(chatMessage),
        });
        this.messageContent = '';
      } catch (e) {
        console.error('No se pudo publicar el mensaje:', e);
      }
    },

    getDisplayName(sender) {
      if (sender === this.username && this.nombreEmpleado) return this.nombreEmpleado;
      return sender || '';
    },

    async confirmDelete(msg) {
      const ok = await this.$bvModal.msgBoxConfirm('¿Estás seguro de que quieres borrar este mensaje?', {
        title: 'Borrar mensaje',
        okVariant: 'danger',
        okTitle: 'Borrar',
        cancelTitle: 'Cancelar',
        centered: true
      });
      if (ok) {
        this.deleteMessage(msg);
      }
    },

    async deleteMessage(msg) {
      if (!msg.id) return;
      try {
        // 1. Delete from DB
        await api1.delete(`api/chat/oferta/${msg.id}`);
        
        // Optimistic UI update
        this.messages = this.messages.filter(m => m.id != msg.id);

        // 2. Broadcast via WS
        await api1.post(`api/chat/oferta/broadcast-delete/${this.ofertaId}/${msg.id}`);
      } catch (error) {
        console.error('Error al borrar el mensaje:', error);
        this.$bvToast.toast('No se pudo borrar el mensaje', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
  },
  mounted() {
    this.cargarNombreEmpleado().finally(() => this.connect());
  },
  beforeDestroy() {
    this.disconnect();
  },
};
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.chat-window {
  display: flex;
  flex-direction: column;
  height: 60vh;
  border: 1px solid #eee;
  border-radius: 4px;
  background-color: white;
  overflow: hidden;
}
.messages-display {
  flex-grow: 1;
  padding: 15px;
  overflow-y: auto;
  border-bottom: 1px solid #eee;
  display: flex;
  flex-direction: column;
}
.message-input-section {
  display: flex;
  padding: 15px;
  border-top: 1px solid #eee;
}
.message-input-section input {
  flex-grow: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-right: 10px;
}
.my-message {
  background-color: #e0f7fa;
  border-radius: 8px;
  padding: 8px 12px;
  max-width: 70%;
}
.other-message {
  background-color: #f1f1f1;
  border-radius: 8px;
  padding: 8px 12px;
  max-width: 70%;
}
.message-wrapper {
  display: flex;
  margin-bottom: 8px;
  align-items: center;
}
.message-wrapper.my-message {
  align-self: flex-end;
  margin-left: auto;
  flex-direction: row;
}
.message-wrapper.other-message {
  align-self: flex-start;
  margin-right: auto;
}
.delete-msg-btn {
  opacity: 0;
  transition: opacity 0.2s;
  font-size: 0.8rem;
}
.message-wrapper:hover .delete-msg-btn {
  opacity: 1;
}
.message-bubble {
  flex-grow: 1;
}
.system-message {
  font-style: italic;
  color: #666;
  text-align: center;
  padding: 5px 0;
  margin-bottom: 10px;
  width: 100%;
}
.date-divider {
  text-align: center;
  font-size: 0.85rem;
  color: #666;
  margin: 12px 0;
  width: 100%;
}
.autocomplete-dropdown {
  position: absolute;
  bottom: 100%;
  left: 10px;
  right: 10px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 -4px 10px rgba(0,0,0,0.1);
}
.autocomplete-dropdown .list-group-item:hover {
  background-color: #e8f0fe !important;
  color: #000 !important;
  cursor: pointer;
}
.message-sender {
  font-weight: bold;
}
</style>
